import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import type { DynamicField, DynamicFieldType } from './models';
import { formValueToApiDateOnly, parseLocalDate } from './date-utils';

/** Ordena campos dinámicos por `displayOrder`. */
export function sortDynamicFields(fields: DynamicField[] | null | undefined): DynamicField[] {
  return [...(fields ?? [])]
    .filter((f) => !!f?.fieldKey?.trim())
    .sort((a, b) => (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER));
}

/** Validador de regex sobre el valor string del control. */
export function dynamicFieldRegexValidator(regex: string | null | undefined): ValidatorFn {
  const pattern = String(regex ?? '').trim();
  if (!pattern) return () => null;
  let compiled: RegExp;
  try {
    compiled = new RegExp(pattern);
  } catch {
    return () => ({ invalidRegexConfig: true });
  }
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') return null;
    const text =
      value instanceof Date
        ? (formValueToApiDateOnly(value) ?? '')
        : String(value).trim();
    if (!text) return null;
    return compiled.test(text) ? null : { pattern: true };
  };
}

function defaultValueForType(type: DynamicFieldType | undefined, existing: unknown): unknown {
  if (existing !== undefined && existing !== null) {
    if (type === 'DATE') {
      return typeof existing === 'string' ? parseLocalDate(existing) : existing;
    }
    if (type === 'BOOLEAN') {
      return existing === true || existing === 'true' || existing === 1 || existing === '1';
    }
    if (type === 'NUMBER') {
      const n = typeof existing === 'number' ? existing : Number(existing);
      return Number.isFinite(n) ? n : null;
    }
    return existing;
  }
  switch (type) {
    case 'BOOLEAN':
      return false;
    case 'NUMBER':
      return null;
    case 'DATE':
      return null;
    default:
      return '';
  }
}

/**
 * Construye (o reconstruye) un FormGroup de campos adicionales según la definición de la empresa.
 * Conserva valores previos cuando la clave coincide.
 */
export function rebuildAdditionalFieldsForm(
  fb: FormBuilder,
  form: FormGroup,
  fields: DynamicField[],
  existingValues?: Record<string, unknown> | null
): void {
  const previous = form.getRawValue() as Record<string, unknown>;
  Object.keys(form.controls).forEach((key) => form.removeControl(key));

  for (const field of fields) {
    const key = String(field.fieldKey).trim();
    const type = field.type ?? 'TEXT';
    const existing =
      existingValues && Object.prototype.hasOwnProperty.call(existingValues, key)
        ? existingValues[key]
        : previous[key];
    const validators = [];
    if (field.required && type !== 'BOOLEAN') {
      validators.push(Validators.required);
    }
    if (field.validationRegex?.trim()) {
      validators.push(dynamicFieldRegexValidator(field.validationRegex));
    }
    form.addControl(key, fb.control(defaultValueForType(type, existing), validators));
  }
}

/** Serializa el FormGroup a un mapa listo para el API. */
export function serializeAdditionalFields(
  fields: DynamicField[],
  form: FormGroup
): Record<string, unknown> | undefined {
  if (!fields.length) return undefined;
  const raw = form.getRawValue() as Record<string, unknown>;
  const result: Record<string, unknown> = {};

  for (const field of fields) {
    const key = String(field.fieldKey).trim();
    const type = field.type ?? 'TEXT';
    const value = raw[key];

    if (type === 'BOOLEAN') {
      result[key] = value === true;
      continue;
    }
    if (value == null || value === '') {
      continue;
    }
    if (type === 'DATE') {
      const date = formValueToApiDateOnly(value);
      if (date) result[key] = date;
      continue;
    }
    if (type === 'NUMBER') {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isFinite(n)) result[key] = n;
      continue;
    }
    result[key] = String(value).trim();
  }

  return Object.keys(result).length ? result : {};
}

export function additionalFieldControl(form: FormGroup, fieldKey: string | undefined): FormControl | null {
  if (!fieldKey) return null;
  return form.get(fieldKey) as FormControl | null;
}

/** Etiqueta corta del tipo de dato para el addon del input group. */
export function dynamicFieldTypeLabel(type: DynamicFieldType | undefined): string {
  switch (type) {
    case 'NUMBER':
      return 'Número';
    case 'DATE':
      return 'Fecha';
    case 'BOOLEAN':
      return 'Booleano';
    case 'SELECT':
      return 'Lista';
    default:
      return 'Texto';
  }
}
